/**
 * Partner engagement block (build-specs/partner-engagement-analytics.md) —
 * lives on the Reports → Referrals page under the referral trend + partners
 * table. Touches are THE CORE METRIC (weekly sparkline per partner, from
 * operating `sections.partners`), rendered side by side with the partner's
 * OWN weekly referral sparkline (`weekly_new_referrals`, the additive field
 * on the referral report); pies are the fun-but-flagged touch type (gold
 * dots + a per-partner symmetric lift table — see buildPieLift for the
 * window convention). The quiet-partner flag is the actionable alarm: a
 * partner still sending referrals whom we have stopped touching.
 *
 * Renders ONLY when partner data exists — an absent/empty `sections.partners`
 * hides the whole block and the page degrades to the core referral view.
 *
 * REGISTER COLOR LAW: forest (sage-800) = referrals; gold (warm-400) =
 * touches / pies; pepper appears ONLY as the muted quiet-flag TEXT — never a
 * fill (matches OperatingSections.tsx).
 */

import { type ReactNode } from 'react';
import type {
  WeeklyOperatingMetricWire,
  ReferralPartnerRowWire,
  ReferralWeekWire,
} from '@/lib/api';
import {
  buildPartnerEngagement,
  buildPieLift,
  aggregateTouchesByWeek,
  readPartnerAliases,
  type PartnerEngagementRow,
} from './partnerEngagement';

const FOREST = 'var(--color-sage-800, #1B4332)';
const GOLD = 'var(--color-warm-400, #C4A265)';
const PEPPER = 'var(--color-pepper, #C1512D)';

const CARD = 'bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4';

function formatWeekEnd(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function LegendDot({ color, label }: { color: string; label: string }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

/** Scaled [x,y] coordinates (0..100 box, y inverted) for a numeric series. */
function coords(series: number[]): Array<{ x: number; y: number }> {
  const max = series.length ? Math.max(...series) : 0;
  const min = series.length ? Math.min(...series) : 0;
  const span = max - min || 1;
  const n = series.length;
  return series.map((v, i) => ({
    x: n <= 1 ? 50 : (i / (n - 1)) * 100,
    y: 100 - ((v - min) / span) * 100,
  }));
}

/** A bare polyline sparkline (no markers) over a 0..100 box. */
function AggSparkline({ series, color, testId }: { series: number[]; color: string; testId: string }): ReactNode {
  const pts = coords(series);
  const line = pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  return (
    <svg data-testid={testId} viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full" aria-hidden>
      {line && <polyline points={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

/**
 * Per-partner touch sparkline with gold pie-week dots overlaid. The polyline
 * lives in a stretch SVG (preserveAspectRatio none); the pie dots are HTML
 * spans positioned by percent so they stay round.
 */
function TouchSparkline({ series, pieWeeks }: { series: number[]; pieWeeks: boolean[] }): ReactNode {
  const pts = coords(series);
  const line = pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  return (
    <div className="relative h-6 w-24 inline-block align-middle">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden>
        {line && <polyline points={line} fill="none" stroke={GOLD} strokeWidth="2" vectorEffect="non-scaling-stroke" />}
      </svg>
      {pts.map((p, i) =>
        pieWeeks[i] ? (
          <span
            key={i}
            data-testid="partner-pie-dot"
            title="Pie delivered"
            className="absolute h-1.5 w-1.5 rounded-full -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${p.x}%`, top: `${p.y}%`, backgroundColor: GOLD }}
          />
        ) : null,
      )}
    </div>
  );
}

function num(n: number | null, digits = 0): string {
  if (n === null || !Number.isFinite(n)) return '–';
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

function percent(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '–';
  return `${Math.round(n * 100)}%`;
}

/** Per-partner referrals sparkline (forest) — the partner's OWN weekly series
 *  from the referral report's additive `weekly_new_referrals` field. */
function ReferralSparkline({ series }: { series: number[] | null }): ReactNode {
  if (series === null || series.length === 0) return <span className="text-ink-soft">–</span>;
  const pts = coords(series);
  const line = pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  return (
    <div className="relative h-6 w-24 inline-block align-middle">
      <svg
        data-testid="partner-referral-sparkline"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        aria-hidden
      >
        {line && <polyline points={line} fill="none" stroke={FOREST} strokeWidth="2" vectorEffect="non-scaling-stroke" />}
      </svg>
    </div>
  );
}

function PartnerRow({ row }: { row: PartnerEngagementRow }): ReactNode {
  return (
    <tr data-testid="partner-engagement-row" className="border-b border-sage-800/[5%] last:border-0">
      <td className="py-2 pr-3 text-ink">
        {row.name}
        {row.tier && <span className="ml-1.5 text-[11px] text-sage-400">T{row.tier}</span>}
        {row.quiet && (
          <span data-testid="partner-quiet-flag" className="ml-2 text-[11px] font-medium" style={{ color: PEPPER }}>
            quiet
          </span>
        )}
      </td>
      <td className="py-2 px-3">
        <TouchSparkline series={row.touchSeries} pieWeeks={row.pieWeeks} />
      </td>
      <td className="py-2 px-3">
        <ReferralSparkline series={row.referralWeekly} />
      </td>
      <td className="py-2 px-3 text-right tabular-nums text-ink font-semibold">{row.totalTouches}</td>
      <td className="py-2 px-3 text-right tabular-nums text-ink">{num(row.referralWindowTotal)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-ink-soft">{num(row.referralRecentAvg, 1)}</td>
      <td className="py-2 pl-3 text-right tabular-nums text-ink-soft">{percent(row.conversionRate)}</td>
    </tr>
  );
}

export function PartnerEngagementBlock({
  operatingRows,
  referralPartners,
  referralBuckets,
  config,
}: {
  operatingRows: WeeklyOperatingMetricWire[];
  referralPartners: ReferralPartnerRowWire[];
  referralBuckets: ReferralWeekWire[];
  config: unknown;
}): ReactNode {
  const aliases = readPartnerAliases(config);
  const { rows, weekEndings } = buildPartnerEngagement(operatingRows, referralPartners, aliases);

  // Graceful degradation: no partner data → render nothing.
  if (rows.length === 0) return null;

  const aggTouches = aggregateTouchesByWeek(operatingRows);
  const aggReferrals = [...referralBuckets].map((b) => b.new_referrals);
  const lift = buildPieLift(rows, referralBuckets);
  const windowLabel =
    weekEndings.length > 0 ? `${formatWeekEnd(weekEndings[0]!)} – ${formatWeekEnd(weekEndings[weekEndings.length - 1]!)}` : '';

  return (
    <div data-testid="partner-engagement" className="space-y-4">
      {/* Aggregate: does nurture (touches) track referrals? Two sparklines side by side. */}
      <div data-testid="partner-engagement-agg" className={CARD}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg text-sage-800">Partner engagement</h2>
          <div className="flex items-center gap-3 text-[11px] text-ink-soft">
            <LegendDot color={GOLD} label="Touches" />
            <LegendDot color={FOREST} label="Referrals" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 mb-1">Touches / week</div>
            <div className="h-16">
              <AggSparkline series={aggTouches} color={GOLD} testId="agg-touches-line" />
            </div>
          </div>
          <div>
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 mb-1">Referrals / week</div>
            <div className="h-16">
              <AggSparkline series={aggReferrals} color={FOREST} testId="agg-referrals-line" />
            </div>
          </div>
        </div>
        {windowLabel && <p className="mt-2 text-[11px] text-ink-soft">{windowLabel}, weekly grain</p>}
      </div>

      {/* Per-partner: touches sparkline (core) + pie dots + referral rollup + quiet flag. */}
      <div className={CARD}>
        <div className="overflow-x-auto">
          <table data-testid="partner-engagement-table" className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 border-b border-sage-800/[8%]">
                <th className="py-2 pr-3">Partner</th>
                <th className="py-2 px-3">
                  Touches{' '}
                  <span className="normal-case tracking-normal font-normal text-warm-400">🥧 = pie</span>
                </th>
                <th className="py-2 px-3">Referrals / wk</th>
                <th className="py-2 px-3 text-right">Total</th>
                <th className="py-2 px-3 text-right">Referrals</th>
                <th className="py-2 px-3 text-right">Recent avg</th>
                <th className="py-2 pl-3 text-right">Conv.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <PartnerRow key={r.name} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pie lift — THIS partner's referrals over symmetric windows around each
          pie (pie week excluded from both sides; equal observed-week counts —
          see partnerEngagement.ts buildPieLift). */}
      {lift.rows.length > 0 && (
        <div className={CARD}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg text-sage-800">Pie lift</h2>
            <p data-testid="partner-lift-topline" className="text-xs text-ink-soft tabular-nums">
              {lift.topline.referrals} referrals · {lift.topline.pies} pies ·{' '}
              <span className="font-semibold text-ink">
                {lift.topline.ratio === null ? '–' : `${num(lift.topline.ratio, 1)} referrals / pie`}
              </span>
            </p>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="partner-lift-table" className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 border-b border-sage-800/[8%]">
                  <th className="py-2 pr-3">Partner</th>
                  <th className="py-2 px-3">Pie</th>
                  <th className="py-2 px-3 text-right">Before</th>
                  <th className="py-2 px-3 text-right">After</th>
                  <th className="py-2 px-3 text-right">Δ</th>
                  <th className="py-2 pl-3 text-right">Window</th>
                </tr>
              </thead>
              <tbody>
                {lift.rows.map((l, i) => {
                  const comparable = l.before !== null && l.after !== null;
                  const delta = comparable ? l.after! - l.before! : null;
                  return (
                    <tr key={`${l.partner}-${l.pie}-${i}`} data-testid="partner-lift-row" className="border-b border-sage-800/[5%] last:border-0">
                      <td className="py-2 pr-3 text-ink">{l.partner}</td>
                      <td className="py-2 px-3 text-ink-soft tabular-nums">{formatWeekEnd(l.pie)}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-ink-soft">{l.before ?? '–'}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-ink-soft">{l.after ?? '–'}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold text-ink">
                        {comparable ? (
                          <>
                            {delta! >= 0 ? '+' : ''}
                            {delta}
                          </>
                        ) : l.tooEarly ? (
                          <span data-testid="partner-lift-too-early" className="font-normal text-ink-soft">
                            too early
                          </span>
                        ) : l.noJoin ? (
                          <span className="font-normal text-ink-soft">no referral match</span>
                        ) : (
                          <span className="font-normal text-ink-soft">no baseline</span>
                        )}
                      </td>
                      <td className="py-2 pl-3 text-right tabular-nums text-ink-soft">
                        {comparable || l.tooEarly ? `${l.observedWeeks} of 13 wks` : '–'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p data-testid="partner-engagement-caveat" className="text-[12px] text-ink-soft italic">
        Directional – small n; touches and pies are nurture signals, not attribution.
      </p>
    </div>
  );
}
